import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
  Button,
  ButtonGroup,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShowChart,
  ArrowDropUp,
  ArrowDropDown
} from '@mui/icons-material';
import './CryptoPriceOverview.css';

// Cryptocurrency data with price history
const cryptoData = [
  {
    id: 'bitcoin',
    symbol: 'BTC',
    name: 'Bitcoin',
    image: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
    current_price: 62145.78,
    market_cap: 1215000000000,
    market_cap_rank: 1,
    price_change_percentage_24h: -1.23,
    price_change_percentage_7d: 2.45,
    price_change_percentage_30d: 8.76,
    sparkline_7d: generateSparklineData(62000, 0.06, -0.01),
    color: '#F7931A'
  },
  {
    id: 'ethereum',
    symbol: 'ETH',
    name: 'Ethereum',
    image: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
    current_price: 3521.45,
    market_cap: 422000000000,
    market_cap_rank: 2,
    price_change_percentage_24h: 2.34,
    price_change_percentage_7d: 5.67,
    price_change_percentage_30d: 12.45,
    sparkline_7d: generateSparklineData(3500, 0.08, 0.02),
    color: '#627EEA'
  },
  {
    id: 'solana',
    symbol: 'SOL',
    name: 'Solana',
    image: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
    current_price: 124.56,
    market_cap: 53000000000,
    market_cap_rank: 5,
    price_change_percentage_24h: 5.67,
    price_change_percentage_7d: 12.34,
    price_change_percentage_30d: 18.92,
    sparkline_7d: generateSparklineData(125, 0.12, 0.04),
    color: '#9945FF'
  },
  {
    id: 'avalanche',
    symbol: 'AVAX',
    name: 'Avalanche',
    image: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
    current_price: 35.78,
    market_cap: 12800000000,
    market_cap_rank: 11,
    price_change_percentage_24h: 3.21,
    price_change_percentage_7d: 8.45,
    price_change_percentage_30d: 15.67,
    sparkline_7d: generateSparklineData(36, 0.10, 0.03),
    color: '#E84142'
  },
  {
    id: 'polygon',
    symbol: 'MATIC',
    name: 'Polygon',
    image: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png',
    current_price: 0.89,
    market_cap: 8200000000,
    market_cap_rank: 15,
    price_change_percentage_24h: -2.45,
    price_change_percentage_7d: -5.67,
    price_change_percentage_30d: -8.92,
    sparkline_7d: generateSparklineData(0.9, 0.15, -0.02),
    color: '#8247E5'
  },
  {
    id: 'chainlink',
    symbol: 'LINK',
    name: 'Chainlink',
    image: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
    current_price: 18.34,
    market_cap: 10500000000,
    market_cap_rank: 13,
    price_change_percentage_24h: 1.56,
    price_change_percentage_7d: 4.32,
    price_change_percentage_30d: 9.87,
    sparkline_7d: generateSparklineData(18.5, 0.09, 0.01),
    color: '#2A5ADA'
  }
];

// Generate sparkline data for 7 days
function generateSparklineData(basePrice, volatility, trendBias) {
  const data = [];
  let price = basePrice;

  for (let i = 0; i < 168; i++) { // 24 hours * 7 days = 168 hours
    const hourlyBias = trendBias * (1 + (Math.random() - 0.5) * 0.5);
    const hourlyVolatility = volatility * (1 + (Math.random() - 0.5) * 0.3);

    price = price * (1 + (Math.random() - 0.5 + hourlyBias) * hourlyVolatility);
    data.push(price);
  }

  return data;
}

// Format large numbers with appropriate suffixes
function formatNumber(num) {
  if (num >= 1000000000) {
    return '$' + (num / 1000000000).toFixed(1) + 'B';
  } else if (num >= 1000000) {
    return '$' + (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return '$' + (num / 1000).toFixed(1) + 'K';
  } else {
    return '$' + num.toFixed(2);
  }
}

// Format price with appropriate decimal places
function formatPrice(price) {
  if (price >= 1000) {
    return '$' + price.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  } else if (price >= 100) {
    return '$' + price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } else if (price >= 1) {
    return '$' + price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  } else {
    return '$' + price.toLocaleString('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    });
  }
}

const CryptoPriceOverview = () => {
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle time range change
  const handleTimeRangeChange = (range) => {
    setIsLoading(true);
    setTimeRange(range);

    // Simulate loading delay
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  // Handle crypto selection
  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto.id === selectedCrypto ? null : crypto.id);
  };

  // Get price change percentage based on selected time range
  const getPriceChangePercentage = (crypto) => {
    switch (timeRange) {
      case '24h':
        return crypto.price_change_percentage_24h;
      case '7d':
        return crypto.price_change_percentage_7d;
      case '30d':
        return crypto.price_change_percentage_30d;
      default:
        return crypto.price_change_percentage_24h;
    }
  };

  // Render mini sparkline chart
  const renderSparkline = (data, color, isPositive) => {
    // Use inline SVG with fixed dimensions
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        className="sparkline"
      >
        <defs>
          <linearGradient
            id={`sparkline-${color.replace('#', '')}-${Math.random().toString(36).substr(2, 9)}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={color} stopOpacity="0.5" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Generate the sparkline path */}
        {(() => {
          // Calculate min/max for scaling
          const maxValue = Math.max(...data);
          const minValue = Math.min(...data);
          const range = maxValue - minValue;

          // Generate points for the line
          const points = data.map((value, index) => {
            const x = (index / (data.length - 1)) * 100;
            const y = 40 - ((value - minValue) / range) * 30; // Leave space at top and bottom
            return `${x},${y}`;
          }).join(' ');

          // Generate area path
          const areaPath = `M0,40 L${points} L100,40 Z`;

          // Generate unique gradient ID
          const gradientId = `sparkline-${color.replace('#', '')}-${Math.random().toString(36).substr(2, 9)}`;

          return (
            <>
              {/* Area under the line */}
              <path
                d={areaPath}
                fill={`url(#${gradientId})`}
              />

              {/* Line */}
              <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          );
        })()}
      </svg>
    );
  };

  // Render detailed chart for selected crypto
  const renderDetailedChart = (crypto) => {
    return (
      <div className="detailed-chart-container">
        <div className="detailed-chart-header">
          <div className="detailed-chart-title">
            <img src={crypto.image} alt={crypto.name} className="detailed-chart-icon" />
            <Typography variant="h6">{crypto.name} ({crypto.symbol})</Typography>
          </div>
          <Typography variant="h5" className={getPriceChangePercentage(crypto) >= 0 ? 'positive-price' : 'negative-price'}>
            {formatPrice(crypto.current_price)}
          </Typography>
        </div>

        <div className="detailed-chart-stats">
          <div className="stat-item">
            <Typography variant="caption">Market Cap</Typography>
            <Typography variant="body2">{formatNumber(crypto.market_cap)}</Typography>
          </div>
          <div className="stat-item">
            <Typography variant="caption">Rank</Typography>
            <Typography variant="body2">#{crypto.market_cap_rank}</Typography>
          </div>
          <div className="stat-item">
            <Typography variant="caption">24h Change</Typography>
            <Typography variant="body2" className={crypto.price_change_percentage_24h >= 0 ? 'positive-price' : 'negative-price'}>
              {crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_percentage_24h.toFixed(2)}%
            </Typography>
          </div>
          <div className="stat-item">
            <Typography variant="caption">7d Change</Typography>
            <Typography variant="body2" className={crypto.price_change_percentage_7d >= 0 ? 'positive-price' : 'negative-price'}>
              {crypto.price_change_percentage_7d >= 0 ? '+' : ''}{crypto.price_change_percentage_7d.toFixed(2)}%
            </Typography>
          </div>
        </div>

        <div className="detailed-chart">
          {/* Use inline SVG with fixed dimensions */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 600 200"
            preserveAspectRatio="none"
          >
            {(() => {
              // Calculate min/max for scaling with padding
              const data = crypto.sparkline_7d;
              const maxValue = Math.max(...data) * 1.05; // Add 5% padding at the top
              const minValue = Math.min(...data) * 0.95; // Add 5% padding at the bottom
              const range = maxValue - minValue;

              // Generate points for the line
              const points = data.map((value, index) => {
                const x = (index / (data.length - 1)) * 600;
                const y = 200 - ((value - minValue) / range) * 180; // Leave space at top and bottom
                return `${x},${y}`;
              }).join(' ');

              // Generate area path
              const areaPath = `M0,200 L${points} L600,200 Z`;

              // Generate unique gradient ID
              const gradientId = `detailed-gradient-${crypto.id}-${Math.random().toString(36).substr(2, 9)}`;

              // Generate price lines
              const priceLines = [];
              const numPriceLines = 5;
              for (let i = 0; i < numPriceLines; i++) {
                const price = minValue + (range / (numPriceLines - 1)) * i;
                priceLines.push(price);
              }

              return (
                <>
                  <defs>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={crypto.color} stopOpacity="0.5" />
                      <stop offset="100%" stopColor={crypto.color} stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Background */}
                  <rect
                    x="0"
                    y="0"
                    width="600"
                    height="200"
                    fill="rgba(0, 0, 0, 0.1)"
                    rx="4"
                    ry="4"
                  />

                  {/* Price lines */}
                  {priceLines.map((price, index) => (
                    <g key={`priceline-${index}`}>
                      <line
                        x1="0"
                        y1={200 - ((price - minValue) / range) * 180}
                        x2="600"
                        y2={200 - ((price - minValue) / range) * 180}
                        stroke="rgba(255, 255, 255, 0.1)"
                        strokeWidth="1"
                        strokeDasharray={index === 0 || index === numPriceLines - 1 ? "none" : "5,5"}
                      />
                      <text
                        x="550"
                        y={200 - ((price - minValue) / range) * 180 - 5}
                        fill="rgba(255, 255, 255, 0.7)"
                        fontSize="12"
                        textAnchor="end"
                      >
                        {formatPrice(price)}
                      </text>
                    </g>
                  ))}

                  {/* Area under the line */}
                  <path
                    d={areaPath}
                    fill={`url(#${gradientId})`}
                  />

                  {/* Line */}
                  <polyline
                    points={points}
                    fill="none"
                    stroke={crypto.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Data points - only show every 24th point (daily) */}
                  {data.filter((_, i) => i % 24 === 0).map((value, index) => {
                    const x = (index * 24 / (data.length - 1)) * 600;
                    const y = 200 - ((value - minValue) / range) * 180;

                    return (
                      <circle
                        key={`point-${index}`}
                        cx={x}
                        cy={y}
                        r="3"
                        fill={crypto.color}
                        stroke="rgba(0, 0, 0, 0.3)"
                        strokeWidth="1"
                      />
                    );
                  })}
                </>
              );
            })()}
          </svg>
        </div>

        <div className="detailed-chart-footer">
          <Typography variant="caption">7-Day Price History</Typography>
          <Typography variant="caption">
            {new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString()} - {new Date().toLocaleDateString()}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div className="crypto-price-overview">
      <Box className="overview-header">
        <Typography variant="h6">Cryptocurrency Prices</Typography>
        <ButtonGroup variant="outlined" size="small" className="time-range-buttons">
          <Button
            className={timeRange === '24h' ? 'active' : ''}
            onClick={() => handleTimeRangeChange('24h')}
          >
            24h
          </Button>
          <Button
            className={timeRange === '7d' ? 'active' : ''}
            onClick={() => handleTimeRangeChange('7d')}
          >
            7d
          </Button>
          <Button
            className={timeRange === '30d' ? 'active' : ''}
            onClick={() => handleTimeRangeChange('30d')}
          >
            30d
          </Button>
        </ButtonGroup>
      </Box>

      <Grid container spacing={2}>
        {/* Crypto price cards */}
        <Grid item xs={12} md={selectedCrypto ? 6 : 12}>
          <div className={`crypto-list ${isLoading ? 'loading' : ''}`}>
            {cryptoData.map(crypto => {
              const priceChange = getPriceChangePercentage(crypto);
              const isPositive = priceChange >= 0;

              return (
                <Paper
                  key={crypto.id}
                  className={`crypto-card ${selectedCrypto === crypto.id ? 'selected' : ''}`}
                  onClick={() => handleCryptoSelect(crypto)}
                >
                  <div className="crypto-info">
                    <div className="crypto-name-container">
                      <img src={crypto.image} alt={crypto.name} className="crypto-icon" />
                      <div className="crypto-name">
                        <Typography variant="body1">{crypto.name}</Typography>
                        <Typography variant="caption" className="crypto-symbol">{crypto.symbol}</Typography>
                      </div>
                    </div>

                    <div className="crypto-price-container">
                      <Typography variant="body1" className="crypto-price">
                        {formatPrice(crypto.current_price)}
                      </Typography>
                      <div className="crypto-change">
                        <Chip
                          icon={isPositive ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                          label={`${isPositive ? '+' : ''}${priceChange.toFixed(2)}%`}
                          size="small"
                          className={isPositive ? 'positive-change' : 'negative-change'}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="sparkline-container">
                    {renderSparkline(crypto.sparkline_7d, crypto.color, isPositive)}
                  </div>
                </Paper>
              );
            })}
          </div>
        </Grid>

        {/* Detailed chart for selected crypto */}
        {selectedCrypto && (
          <Grid item xs={12} md={6}>
            <Paper className="detailed-chart-paper">
              {renderDetailedChart(cryptoData.find(crypto => crypto.id === selectedCrypto))}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Market overview section */}
      <Box mt={4}>
        <Typography variant="h6" className="section-title">Market Overview</Typography>
        <Paper className="market-overview-paper">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <div className="market-stat">
                <Typography variant="body2" className="stat-label">Total Market Cap</Typography>
                <Typography variant="h5" className="stat-value">$2.56T</Typography>
                <div className="stat-change positive">
                  <ArrowDropUp />
                  <Typography variant="body2">+1.2% (24h)</Typography>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className="market-stat">
                <Typography variant="body2" className="stat-label">24h Trading Volume</Typography>
                <Typography variant="h5" className="stat-value">$98.7B</Typography>
                <div className="stat-change negative">
                  <ArrowDropDown />
                  <Typography variant="body2">-3.5% (24h)</Typography>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className="market-stat">
                <Typography variant="body2" className="stat-label">BTC Dominance</Typography>
                <Typography variant="h5" className="stat-value">42.3%</Typography>
                <div className="stat-change negative">
                  <ArrowDropDown />
                  <Typography variant="body2">-0.8% (24h)</Typography>
                </div>
              </div>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </div>
  );
};

export default CryptoPriceOverview;
